"use client";

import { useEffect, useId, useMemo, useRef, useState } from "react";
import type { Locale } from "@/i18n/config";
import { ISO3166_ALPHA2 } from "@/lib/data/countries-iso";

interface CountrySelectProps {
  value: string;
  onChange: (code: string) => void;
  locale: Locale;
  id?: string;
  placeholder?: string;
}

function localeTag(locale: Locale): string {
  switch (locale) {
    case "ko": return "ko";
    case "en": return "en";
    case "ja": return "ja";
    case "zh": return "zh-Hans";
    case "zh-tw": return "zh-Hant";
    default: return "en";
  }
}

function displayName(code: string, locale: Locale): string {
  if (code === "OTHER") return code;
  try {
    const dn = new Intl.DisplayNames([localeTag(locale)], { type: "region" });
    return dn.of(code) ?? code;
  } catch {
    return code;
  }
}

function flagEmoji(code: string): string {
  if (code.length !== 2) return "";
  const A = 0x1f1e6;
  return String.fromCodePoint(
    A + code.charCodeAt(0) - 65,
    A + code.charCodeAt(1) - 65,
  );
}

export function CountrySelect({ value, onChange, locale, id, placeholder }: CountrySelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();

  const selectedLabel = value ? `${flagEmoji(value)} ${displayName(value, locale)}` : "";
  const selectedCode = value || "";

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [...ISO3166_ALPHA2];
    return ISO3166_ALPHA2.filter((code) => {
      const name = displayName(code, locale).toLowerCase();
      return code.toLowerCase().includes(q) || name.includes(q);
    });
  }, [query, locale]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const handleSelect = (code: string) => {
    onChange(code);
    setOpen(false);
    setQuery("");
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          type="text"
          readOnly={!open}
          value={open ? query : selectedLabel}
          placeholder={open ? (placeholder ?? "Search country") : (placeholder ?? displayName("KR", locale))}
          onFocus={() => {
            setOpen(true);
            setQuery("");
            // select text for quick replace when reopening
            requestAnimationFrame(() => inputRef.current?.select());
          }}
          onChange={(e) => {
            if (!open) setOpen(true);
            setQuery(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setOpen(false);
              setQuery("");
              (e.target as HTMLInputElement).blur();
            }
          }}
          className="field-input pr-10 cursor-pointer focus:cursor-text"
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-controls={listboxId}
        />
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-faint">
          {selectedCode && !open ? (
            <span className="text-xs tracking-widest">{selectedCode}</span>
          ) : (
            <svg width="12" height="8" viewBox="0 0 12 8" aria-hidden>
              <path d="M1 1l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="1.4" />
            </svg>
          )}
        </span>
      </div>

      {open && (
        <div className="absolute left-0 right-0 z-30 mt-1 max-h-60 overflow-auto rounded-md border border-line bg-paper shadow-[0_20px_60px_-20px_rgba(35,33,29,0.35)]">
          {filtered.length === 0 ? (
            <p className="px-4 py-3 text-sm text-faint">No results</p>
          ) : (
            <ul id={listboxId} role="listbox" className="py-1">
              {filtered.map((code) => {
                const name = displayName(code, locale);
                const active = code === value;
                return (
                  <li key={code} role="option" aria-selected={active}>
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        // prevent input blur before click
                        e.preventDefault();
                        handleSelect(code);
                      }}
                      className={`flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors hover:bg-canvas ${
                        active ? "bg-mist text-strong" : "text-body"
                      }`}
                    >
                      <span className="text-base leading-none">{flagEmoji(code)}</span>
                      <span className="flex-1 truncate">{name}</span>
                      <span className="text-xs tracking-widest text-faint">{code}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export function countryDisplayName(code: string, locale: Locale): string {
  return displayName(code, locale);
}
